import { StandardEvents } from '@shopify/events';

// Product-page presentation only: plans/prices remain owned by Shopify Subscriptions.
class MibPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.scope = this.closest('.shopify-section') || this.parentElement;
    this.variants = JSON.parse(this.querySelector('[data-plan-data]').textContent).variants;
    this.mode ||= 'subscription';
    this.variantId = this.dataset.variantId;
    this.planId ||= this.querySelector('[data-selling-plan]').value;
    this.addEventListener('click', (event) => {
      // Let the native schedule dropdown open without re-rendering its options.
      if (event.target.closest('[data-plan-select], [data-schedule] label')) return;
      if (event.target.closest('[data-one-time]')) this.mode = 'one-time';
      else if (event.target.closest('[data-subscription-box], [data-return]')) this.mode = 'subscription';
      else return;
      this.render();
    }, { signal });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-purchase-mode]')) {
        this.mode = event.target.value;
        this.render();
        return;
      }
      if (!event.target.matches('[data-plan-select]')) return;
      this.planId = event.target.value;
      this.mode = 'subscription';
      this.render();
    }, { signal });
    this.scope.addEventListener(StandardEvents.productSelect, (event) => this.onVariant(event), { signal });
    // Form-associated inputs cover native and accelerated checkout. This also ensures
    // FormData reads current purchase intent if Horizon has updated its variant input.
    this.scope.addEventListener('formdata', (event) => {
      if (event.target.id !== this.dataset.formId) return;
      const variant = this.variants.find(v => v.id === String(event.formData.get('id')));
      if (!variant) return;
      const plan = this.planFor(variant);
      if (plan && (this.mode === 'subscription' || variant.required)) event.formData.set('selling_plan', plan.id);
      else event.formData.delete('selling_plan');
    }, { capture: true, signal });
    this.render();
  }

  disconnectedCallback() { this.abort?.abort(); }

  planFor(variant) {
    return variant.plans.find(plan => plan.id === this.planId) || variant.plans[0];
  }

  // Readable by the sticky bar even if it connected after our initial render.
  get purchaseSelection() {
    const variant = this.variants?.find(v => v.id === this.variantId);
    if (!variant) return null;
    const plan = this.planFor(variant);
    const subscription = Boolean(plan && (this.mode === 'subscription' || variant.required));
    return {
      variantId: variant.id,
      unitPrice: subscription ? plan.price : variant.price,
      compareAtPrice: subscription ? Math.max(variant.price, variant.compareAtPrice || 0) : variant.compareAtPrice || 0,
      sellingPlanId: subscription ? plan.id : null,
    };
  }

  async onVariant(event) {
    if (event.target.closest('product-card')) return;
    const generation = this.generation = (this.generation || 0) + 1;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.generation) return;
      if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
      this.variantId = detail?.resource?.id == null ? '' : String(detail.resource.id);
      this.render();
    } catch (error) {
      // Horizon owns errors and disables unavailable selections; do not reset intent.
      if (error.name !== 'AbortError') console.warn('Purchase options: variant update failed', error);
    }
  }

  render() {
    const variant = this.variants.find(v => v.id === this.variantId);
    const plan = variant && this.planFor(variant);
    const subscription = Boolean(plan && (this.mode === 'subscription' || variant.required));
    const input = this.querySelector('[data-selling-plan]');
    input.disabled = !subscription;
    input.value = subscription ? plan.id : '';
    this.querySelector('[data-subscription-box]').hidden = !plan;
    this.querySelector('[data-subscription-box]').dataset.selected = String(subscription);
    this.querySelector('[data-autoship]')?.setAttribute('aria-pressed', String(subscription));
    this.querySelector('[data-unavailable]').hidden = Boolean(plan);
    const oneTime = this.querySelector('[data-one-time]');
    oneTime.hidden = !variant || variant.required;
    if (oneTime.matches('button')) oneTime.setAttribute('aria-pressed', String(Boolean(variant && !subscription)));
    oneTime.dataset.selected = String(Boolean(variant && !subscription));
    this.querySelectorAll('[data-purchase-mode]').forEach(radio => {
      const isSubscription = radio.value === 'subscription';
      radio.checked = Boolean(variant && (isSubscription ? subscription : !subscription));
      radio.disabled = isSubscription ? !plan : !variant || variant.required;
    });
    if (variant) oneTime.querySelectorAll('[data-one-time-copy]').forEach(el => {
      el.textContent = el.dataset.oneTimeCopy.replaceAll('[price]', variant.money);
    });
    this.querySelector('[data-one-time-status]').hidden = !variant || subscription;
    this.querySelector('[data-return]').hidden = !plan || subscription;
    this.dispatchEvent(new CustomEvent('mib:purchase-change', { bubbles: true, detail: this.purchaseSelection }));
    if (!plan) return;
    this.planId = plan.id;
    const discount = variant.price ? Math.max(0, Math.round((variant.price - plan.price) * 100 / variant.price)) : 0;
    const fill = text => text.replaceAll('[price]', plan.money).replaceAll('[plan]', plan.name).replaceAll('[discount]', discount + '%');
    this.querySelectorAll('[data-plan-copy]').forEach(el => { el.textContent = fill(el.dataset.planCopy); });
    const regular = this.querySelector('[data-regular-price]');
    regular.textContent = variant.money;
    regular.hidden = plan.price >= variant.price;
    this.querySelector('[data-subscription-price]').textContent = plan.money;
    this.querySelector('[data-schedule]').hidden = variant.plans.length < 2;
    const select = this.querySelector('[data-plan-select]');
    select.replaceChildren(...variant.plans.map(p => new Option(p.name + ' — ' + p.money, p.id, false, p.id === plan.id)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
if (!customElements.get('mib-purchase-options')) customElements.define('mib-purchase-options', MibPurchaseOptions);
